/**
 * 會員訂單紀錄 — LIFF /orders
 *
 * 列出會員自己的訂單(GET /api/orders,最近 20 筆,新到舊)。
 * 每筆卡片可展開看明細:品項 / 金額 / 折抵 / 出貨物流 / 收件資訊。
 *
 * 設計取捨:
 *   - 資料與後台同一個 orders collection;狀態中文 label 沿用 api/admin 的
 *     ORDER_STATUS_LABELS(已對齊後端 order_service),避免前後台用字分歧。
 *   - 詳情做成「就地展開」而非跳頁 — 既有 /orders/:id 是結帳後的「訂單已成立」確認頁
 *     (大勾勾 + 繼續選購 CTA),語氣不適合當歷史查閱;就地展開能反映當下真實狀態。
 *
 * 空狀態:引導去 /products 選購。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt } from "lucide-react";
import StatusMark from "../components/StatusMark";
import { getMyOrders, type Order } from "../api/cart";
import { ORDER_STATUS_LABELS, CARRIER_LABELS } from "../api/admin";
import { useUIStore } from "../store/uiStore";


// 狀態 → 文字顏色(會員端一眼掃)。key 對齊 ORDER_STATUS_LABELS。
const STATUS_COLOR: Record<string, string> = {
  pending_payment: "text-coral",       // 街口單未付款
  pending_payment_mock: "text-coral",
  paid: "text-coral",
  processing: "text-sage",
  partially_shipped: "text-sage",
  shipped: "text-primary",
  completed: "text-primary",
  cancelled: "text-muted",
};


export default function OrderHistory() {
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await getMyOrders();
        setOrders(list);
      } catch {
        showToast("載入訂單失敗", "error");
        setOrders([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [showToast]);

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted text-sm">載入中…</div>;
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button onClick={() => navigate("/home")} className="text-white text-xl" aria-label="返回">‹</button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">訂單紀錄</h1>
          <div className="text-xs opacity-80 mt-0.5">您在霽雲的購買訂單</div>
        </div>
      </header>

      <div className="px-5 mt-5 space-y-3">
        {(!orders || orders.length === 0) ? (
          <EmptyState onShop={() => navigate("/products")} />
        ) : (
          orders.map((o) => <OrderCard key={o.order_id} order={o} />)
        )}
      </div>

      {orders && orders.length > 0 && (
        <div className="px-5 mt-5">
          <button onClick={() => navigate("/products")} className="btn-secondary w-full">
            繼續選購
          </button>
        </div>
      )}
    </div>
  );
}


function OrderCard({ order }: { order: Order }) {
  const [open, setOpen] = useState(false);
  // 空 status(舊單沒寫)→ 視為待出貨,只是顯示 fallback,不寫資料(同後台 OrderList 做法)
  const status = order.status || "pending_payment_mock";
  const statusColor = STATUS_COLOR[status] || "text-ink";
  const statusLabel = ORDER_STATUS_LABELS[status] || status;
  const itemsSummary = formatItemsSummary(order.items);
  const itemCount = (order.items || []).reduce((n, it) => n + (it.qty || 0), 0);

  return (
    <div className="card p-0 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 active:bg-white/70 transition"
      >
        <div className="flex items-center justify-between gap-3">
          <span className={`text-xs font-bold ${statusColor}`}>● {statusLabel}</span>
          <span className="text-xs text-muted">{formatDate(order.created_at)}</span>
        </div>
        <div className="text-sm font-bold text-ink mt-1.5 truncate">{itemsSummary}</div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-muted">共 {itemCount} 件 · NT$ {order.total.toLocaleString()}</span>
          <span className="text-xs text-muted">{open ? "收合 ▴" : "明細 ▾"}</span>
        </div>
        <div className="text-xxs text-muted mt-1.5 font-mono opacity-70">#{order.order_id}</div>
      </button>

      {open && <OrderDetail order={order} />}
    </div>
  );
}


function OrderDetail({ order }: { order: Order }) {
  const addr = order.address || {};
  const addrStr = [addr.city, addr.district, addr.detail].filter(Boolean).join(" ");
  const shipments = order.shipments || [];
  // 有分批出貨 → 逐批列;沒有才 fallback 舊單單筆出貨摘要
  const ship = shipments.length === 0 ? shipmentSummary(order) : null;

  return (
    <div className="border-t border-line px-4 py-4 space-y-4">
      {/* 品項 */}
      <div className="space-y-2">
        {(order.items || []).map((it) => (
          <div key={it.product_id + it.mode} className="flex justify-between gap-3 text-sm">
            <div className="flex-1 min-w-0">
              <div className="text-ink">{it.name}</div>
              <div className="text-xs text-muted">{it.spec} · NT$ {it.price_each.toLocaleString()} × {it.qty}</div>
            </div>
            <div className="text-ink shrink-0">NT$ {it.subtotal.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* 折抵 / 總計 */}
      <div className="border-t border-line pt-3 space-y-1">
        {!!order.discount_amount && order.discount_amount > 0 && (
          <div className="flex justify-between text-xs text-muted">
            <span>折抵{order.applied_code?.label ? ` · ${order.applied_code.label}` : ""}</span>
            <span>− NT$ {order.discount_amount.toLocaleString()}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-muted text-sm">總計</span>
          <span className="text-base font-bold text-ink">NT$ {order.total.toLocaleString()}</span>
        </div>
      </div>

      {/* 出貨紀錄 — 分批出貨逐批列(每批:日期 / 品項瓶數 / 物流單號) */}
      {shipments.length > 0 && (
        <div className="text-xs border-t border-line pt-3 space-y-2">
          <div className="text-muted font-medium">出貨紀錄(共 {shipments.length} 批)</div>
          {shipments.map((sh, idx) => (
            <div key={sh.shipment_id || idx} className="rounded-lg bg-cream/70 p-2.5 space-y-1">
              <div className="flex justify-between text-ink">
                <span className="font-medium">第 {idx + 1} 批</span>
                <span className="text-muted">{sh.ship_date || ""}</span>
              </div>
              {(sh.lines || []).map((ln) => (
                <div key={ln.product_id + (ln.mode || "")} className="flex justify-between text-muted">
                  <span className="truncate">{ln.name}</span>
                  <span className="shrink-0">× {ln.qty} {ln.unit === "bottle" ? "瓶" : "件"}</span>
                </div>
              ))}
              {(sh.carrier || sh.number) && (
                <div className="text-muted pt-0.5">
                  {CARRIER_LABELS[sh.carrier] || sh.carrier || ""}
                  {sh.number ? ` · 單號 ${sh.number}` : ""}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 舊單單筆出貨 legacy 摘要(無分批紀錄才顯示) */}
      {ship && (
        <div className="text-xs text-muted border-t border-line pt-3 space-y-0.5">
          {ship.date && <div>出貨日　{ship.date}</div>}
          {ship.carrier && <div>物流　{ship.carrier}{ship.number ? ` · ${ship.number}` : ""}</div>}
        </div>
      )}

      {/* 收件 */}
      <div className="text-xs text-muted border-t border-line pt-3 space-y-0.5">
        <div>收件人　{order.name || "—"}</div>
        {addrStr && <div>寄送　{addrStr}</div>}
      </div>

      {order.note && (
        <div className="text-xs text-muted border-t border-line pt-3">備註　{order.note}</div>
      )}
    </div>
  );
}


function EmptyState({ onShop }: { onShop: () => void }) {
  return (
    <div className="card text-center py-10 space-y-4">
      <StatusMark icon={Receipt} />
      <div className="text-base text-ink">尚無訂單紀錄</div>
      <div className="text-xs text-muted">選購後,您的訂單會顯示在這裡,方便隨時查閱。</div>
      <button onClick={onShop} className="btn-primary mt-2">前往選購</button>
    </div>
  );
}


// ── helpers ──

function formatItemsSummary(items?: Order["items"]): string {
  if (!items || items.length === 0) return "—";
  const first = items[0];
  const firstStr = `${first.name} × ${first.qty}`;
  if (items.length === 1) return firstStr;
  return `${firstStr} 等 ${items.length} 項`;
}

// 舊單單筆出貨摘要(order.tracking + ship_date)— 有 shipments 的訂單走逐批列表,不進這裡
function shipmentSummary(order: Order): { date?: string; carrier?: string; number?: string } | null {
  if (order.tracking || order.ship_date) {
    const t = order.tracking;
    return {
      date: order.ship_date,
      carrier: t ? CARRIER_LABELS[t.carrier] || t.carrier || "" : "",
      number: t?.number || "",
    };
  }
  return null;
}

function formatDate(raw?: string): string {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString(
      "zh-TW",
      sameYear
        ? { month: "numeric", day: "numeric" }
        : { year: "numeric", month: "numeric", day: "numeric" },
    );
  } catch {
    return "";
  }
}
